/**
 * useCommAssist — the KI-Assistenz data for the Eingang reading pane (Story UI-U9).
 *
 * live mode → GET /communications/:commId/assist via the tolerant fetchCommAssist
 *             (feature-detected; ANY failure/absent/empty → null → block absent).
 * mock mode → deriveMockAssist(mail) from the mock kiZusammenfassung/kiAntwort fields.
 *
 * commId is the SPLIT-VIEW communication id (SplitViewResponse.communication.id),
 * NOT the task id — the caller passes `splitView?.communication?.id ?? null`.
 *
 * [Source: apps/web/src/features/communication-v2/hooks/useEingangMails.ts] (live/mock pattern)
 * [Source: apps/web/src/lib/data-mode.ts]
 * [Source: docs/stories/ui-u9-kommunikation-assistenz.md AC1]
 */

import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchCommAssist, deriveMockAssist } from '../api/comm-assist-api';
import type { CommAssist } from '../api/comm-assist-api';
import type { EingangMail } from '../types';

/** The KI-Assistenz for the selected mail, or null when there is nothing to show. */
export function useCommAssist(commId: string | null, mail: EingangMail | null): CommAssist | null {
  const live = isLiveData();
  // useQuery is ALWAYS called (hooks rule); disabled in mock / when no comm id.
  const { data } = useQuery<CommAssist | null>({
    queryKey: qk.commAssist(commId ?? 'none'),
    queryFn: () => fetchCommAssist(commId as string),
    enabled: live && commId !== null,
    retry: false,
    staleTime: 60_000,
  });

  if (!live) return deriveMockAssist(mail);
  return data ?? null;
}
